<script setup lang="ts">
import { computed, ref, toRef } from 'vue';
import dummyImage1 from '@/assets/dummy-image1.png';
import dummyImage2 from '@/assets/dummy-image2.png';
import dummyImage3 from '@/assets/dummy-image3.png';
import dummyImage4 from '@/assets/dummy-image4.png';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import dayjs from 'dayjs';
import AdpDocumentCardSmall from '@/components/common/article/adp-document-card-small.vue';
import FeedSummaryGenerationButton from '@/components/feed-summary/feed-summary-generation-button.vue';
import FeedSummaryNext from '@/components/feed-summary/feed-summary-next.vue';
import { useTimedProgress } from '@/components/home/use-timed-progress';
import { Article, FeedSummaryProcessedResponse } from '@/types';
import { formatDate } from '@/utils/formatters';
import Divider from '../common/divider.vue';

const props = defineProps<{
  actionCount: number;
  requiredCount: number;
  isPreparingFeed: boolean;
  sentFirstPersonalNewsFeedAt?: string;
}>();

const emit = defineEmits<{
  startClicked: [];
}>();

// パーソナルニュースの生成プログレスは表示のみなのでエラー対応はないです
const hasError = ref(false);
const { progressBarWidth, showProgress, isComplete } = useTimedProgress(
  {
    isGenerating: toRef(props, 'isPreparingFeed'),
    hasError,
    createdAt: computed(() => props.sentFirstPersonalNewsFeedAt),
  },
  { minSec: 300, maxSec: 600 },
);

const publishedAt = dayjs().toISOString();
const dateStr = formatDate(publishedAt);

const buildDummyArticle = (
  id: number,
  title: string,
  mediaName: string,
  imageUrl: string,
): Article => ({
  id,
  media_name: mediaName,
  url: '',
  pdf_url: null,
  image_url: imageUrl,
  title,
  content: '',
  published_at: publishedAt,
  comments: [],
  marks: [],
  group_marks: [],
  theme_id: 0,
  rank: 1,
  hit_keywords: [],
  is_read: false,
  lang: 'ja',
  doc_type: 'article',
  category: '',
  news_categories: null,
  pest_category: null,
});

const featuredArticle = buildDummyArticle(
  -1,
  '全固体電池、量産化へ製造プロセス開発が加速　電解質の低コスト化が焦点に',
  '先端産業テックレポート',
  dummyImage1,
);

const documentArticles = [
  buildDummyArticle(
    -2,
    '次世代半導体パッケージ、ガラス基板の実用化競争が加速　放熱・微細配線で優位に',
    'エレクトロデバイス産業ニュース',
    dummyImage2,
  ),
  buildDummyArticle(
    -3,
    '水素製造コスト、2030年に3分の1へ　大型電解装置の実証運転が本格化',
    'エナジーテック通信',
    dummyImage3,
  ),
  buildDummyArticle(
    -4,
    'ペロブスカイト太陽電池、屋外実証で耐久性が大幅向上　量産化へ前進',
    '素材産業ジャーナル',
    dummyImage4,
  ),
];

const dummySummary: FeedSummaryProcessedResponse = {
  id: -1,
  generation_status: 'processed',
  uuid: 'dummy',
  model_response: `
# 1. 電池材料の脱レアメタル化と資源循環をめぐる新潮流
次世代電池の開発競争が進む中、レアメタルに依存しない材料設計と、使用済み電池からの資源回収を組み合わせた取り組みが広がっている。材料メーカー各社は回収コストの低減と品質の安定化を課題に挙げており、リサイクル材を前提としたサプライチェーンの構築が今後の焦点となる。関連する製造プロセスの実証も各地で始まっており、量産段階での歩留まり確保が次の論点になる見込みだ。

# 2. 半導体パッケージ基板の材料転換、放熱性能が争点に
次世代半導体の高性能化に伴い、パッケージ基板における放熱と微細配線の両立が課題となっている。ガラス基板への転換を軸に、素材メーカー各社は熱伝導特性の改善と量産プロセスの確立を急いでおり、既存のシリコン基板からの置き換えが進むかが今後の注目点となる。

# 3. 水素製造の大型実証が本格化、コスト低減の見通し
水素社会の実現に向け、大型電解装置を用いた実証運転が各地で本格化している。設備の大規模化とエネルギー効率の向上により、2030年までの製造コスト大幅削減が見込まれており、量産体制の構築とサプライチェーンの整備が今後の焦点になる。
`,
  sources: [],
  has_converted_survey: false,
};
</script>
<template>
  <div class="personal-news-onboarding-prompt">
    <div class="header">
      <span class="title">
        <DgrIcon
          name="sparkles-fill"
          :keep-fill="false"
          class="aconnect-home-icon icon"
        />
        <span class="c-title-bold--xm">パーソナルニュース</span>
        <span
          class="c-text-regular--s subtitle"
          :class="{ blurred: !isPreparingFeed }"
          :aria-hidden="!isPreparingFeed"
          >{{ dateStr }}</span
        >
        <span class="c-text-regular--s subtitle"
          >AIが興味に合う記事を毎日お届け。マークするほど精度が上がります。</span
        >
      </span>
      <FeedSummaryGenerationButton
        v-if="!isPreparingFeed"
        state="no-update"
        class="generation-button blurred"
        aria-hidden="true"
      />
    </div>
    <div class="feed blurred" aria-hidden="true">
      <AdpDocumentCardSmall
        class="featured document-card"
        :adp-document="featuredArticle"
        image-variant="banner"
        feed-type="all"
        :show-option-menu="false"
        page-name="home"
        feature="personal_feed"
      />
      <FeedSummaryNext
        class="feed-summary"
        :feed-summary="dummySummary"
        suffix="(07:00更新分)"
        feature="personal_feed"
        page-name="home"
      />
      <div class="documents">
        <AdpDocumentCardSmall
          v-for="article in documentArticles"
          :key="article.id"
          class="document-card"
          :adp-document="article"
          feed-type="all"
          :show-option-menu="false"
          page-name="home"
          feature="personal_feed"
        />
      </div>
    </div>
    <div class="footer blurred" aria-hidden="true">
      <span class="c-btn c-outlineBtn c-outlineBtn--auto c-outlineBtn--small">
        パーソナルニュースをすべて見る
      </span>
    </div>
    <div class="overlay countdown">
      <div class="overlay-icon">
        <DgrIcon name="sparkles-fill" :keep-fill="false" />
      </div>
      <template v-if="isPreparingFeed">
        <div class="c-title-bold--l">10/10 アクション完了！</div>
        <div class="c-text-regular--xm no-wrap">
          あなたにぴったりの「パーソナルニュース」を準備しています。
        </div>
        <div v-if="showProgress" class="progress-wrapper">
          <div class="progress-bar">
            <div class="progress-bar-fill" :class="{ done: isComplete }" />
          </div>
        </div>
        <div class="c-text-regular--s">
          <div>完了まで5〜10分ほど。このまま閉じても大丈夫です。</div>
          <div>準備ができたら左メニューの「お知らせ」でご案内します。</div>
        </div>
      </template>
      <template v-else>
        <div class="c-title-bold--l">あなた専用のニュースを作りましょう</div>
        <div class="c-text-regular--xm">
          閲覧・マーク・コメントなどで、AIがあなたの興味を学習します。10回のアクションで、あなたに合ったニュース・論文・特許が「パーソナルニュース」としてここに届きます。
        </div>
        <Divider />
        <div class="actions">
          <div class="action">
            <DgrIcon name="eye" size="small" :keep-fill="false" />
            <div class="c-title-bold--m">閲覧</div>
            <div class="c-text-regular--s">読むだけでも学習</div>
          </div>
          <div class="action">
            <DgrIcon name="check-circle" size="small" :keep-fill="false" />
            <div class="c-title-bold--m">マーク</div>
            <div class="c-text-regular--s">興味を伝える</div>
          </div>
          <div class="action">
            <DgrIcon name="group-mark" size="small" :keep-fill="false" />
            <div class="c-title-bold--m">グループマーク</div>
            <div class="c-text-regular--s">チームにも共有</div>
          </div>
          <div class="action">
            <DgrIcon name="comment" size="small" :keep-fill="false" />
            <div class="c-title-bold--m">コメント</div>
            <div class="c-text-regular--s">論点まで伝わる</div>
          </div>
        </div>
        <Divider />
        <div class="count-info">
          <span class="marks">
            <span
              v-for="i in requiredCount"
              :key="i"
              class="mark"
              :class="{ filled: i <= actionCount }"
            />
          </span>
          <span class="c-title-medium--xs">
            {{ actionCount }} / {{ requiredCount }}
          </span>
        </div>
        <div class="buttons">
          <RouterLink
            :to="{ name: 'onboardingWelcome' }"
            class="c-text-regular--s"
          >
            セットアップを続ける
          </RouterLink>
          <button
            class="c-btn c-btn--auto c-btn--AnewsPrimary"
            @click="emit('startClicked')"
          >
            今すぐはじめる
          </button>
        </div>
      </template>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.blurred {
  filter: blur(2px);
  pointer-events: none;
  user-select: none;
}

.personal-news-onboarding-prompt {
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 12px 16px 16px 16px;
  border: 1px solid $color-gray400;
  border-radius: 4px;
  background: #ffffff;

  .header {
    display: flex;
    justify-content: space-between;

    .title {
      display: flex;
      gap: 8px;
      align-items: center;

      .subtitle {
        color: $color-gray800;
      }

      .icon {
        color: $color-green600;
      }
    }
  }

  .feed {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-template-areas:
      'featured  summary   summary'
      'documents documents documents';
    gap: 16px;

    .featured {
      grid-area: featured;
      min-width: 0;
      border: 1px solid $color-gray400;
      border-radius: 4px;
    }
    .feed-summary {
      grid-area: summary;
      contain: size;

      // 主要トピックはデフォルトで閉じるため、プレースホルダーでは常に開いた見た目に固定する
      :deep(.container) {
        grid-template-rows: 1fr !important;
      }
      :deep(.inner-container) {
        overflow: visible !important;
      }
    }

    .documents {
      grid-area: documents;
      display: grid;
      grid-template-columns: subgrid;

      .document-card {
        min-width: 0;
        padding: 16px;
        border: 1px solid $color-gray400;
        border-radius: 4px;
      }
    }
  }

  .footer {
    display: flex;
    justify-content: center;
  }

  .overlay {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: fit-content;
    height: fit-content;
    gap: 16px;
    padding: 32px 24px;
    text-align: center;
    background: #ffffff;
    border-radius: 8px;
    border: 1px solid $color-gray400;
    box-shadow: 0 10px 34px 0 rgba(74, 74, 74, 0.14);

    &.countdown {
      padding: 32px 64px;
    }

    .no-wrap {
      text-wrap: nowrap;
    }

    .overlay-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 48px;
      height: 48px;
      border-radius: 50%;
      background: $color-green100;
      color: $color-green600;
    }

    .actions {
      display: flex;
      justify-content: center;
      width: 520px;
      gap: 24px;

      .action {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 4px;

        .icon-box,
        .c-text-regular--s {
          color: $color-gray800;
        }
      }
    }

    .count-info {
      display: flex;
      align-items: center;
      gap: 12px;

      .marks {
        display: flex;
        gap: 8px;
        align-items: center;

        .mark {
          display: inline-flex;
          height: 8px;
          width: 8px;
          border-radius: 8px;
          background-color: $color-gray400;
          pointer-events: none;

          &.filled {
            background-color: $color-p4;
          }
        }
      }
      .c-title-medium--s {
        color: $color-gray800;
      }
    }

    .buttons {
      display: flex;
      align-items: center;
      gap: 8px;

      .c-text-regular--s {
        padding: 8px 16px;
        border-radius: 4px;

        &:hover {
          text-decoration: underline;
        }
      }
    }

    .progress-wrapper {
      width: 280px;
      .progress-bar {
        width: 100%;
        height: 8px;
        background: $color-gray400;
        border-radius: 4px;
        overflow: hidden;
      }

      .progress-bar-fill {
        width: v-bind(progressBarWidth);
        height: 100%;
        background: $color-green600;
        border-radius: 4px;

        &.done {
          transition: width 0.3s ease-out;
        }
      }
    }
  }
}
</style>
